import { motion } from "framer-motion";
import {
  Store,
  Building2,
  HeartPulse,
  Truck,
  Factory,
  Fuel,
  Landmark,
  // ChevronRight,
} from "lucide-react";
import backgroundSvg from "@/assets/cta.svg";

const industries = [
  {
    icon: Store,
    title: "Retail & Distribution",
    description:
      "Manage inventory, sales, procurement, suppliers and multi-store operations from one platform.",
    capabilities: [
      "Inventory Control",
      "POS & Sales",
      "Supplier Management",
      "Multi-Store Reporting",
    ],
  },

  {
    icon: Building2,
    title: "Construction & Real Estate",
    description:
      "Track projects, budgets, procurement, contractors and asset utilization in real time.",
    capabilities: [
      "Project Accounting",
      "Budget Tracking",
      "Procurement",
      "Asset Management",
    ],
  },

  {
    icon: HeartPulse,
    title: "Healthcare",
    description:
      "Improve operational visibility, compliance and financial management across healthcare facilities.",
    capabilities: [
      "Financial Controls",
      "Inventory Tracking",
      "Compliance",
      "Management Reporting",
    ],
  },

  {
    icon: Truck,
    title: "Logistics & Transport",
    description:
      "Monitor fleet operations, expenses, procurement and business performance from a single dashboard.",
    capabilities: [
      "Fleet Operations",
      "Expense Tracking",
      "Revenue Monitoring",
      "Performance Reporting",
    ],
  },

  {
    icon: Factory,
    title: "Manufacturing",
    description:
      "Gain visibility into production, inventory, procurement and operational costs.",
    capabilities: [
      "Production Visibility",
      "Inventory Control",
      "Cost Tracking",
      "Business Intelligence",
    ],
  },

  {
    icon: Fuel,
    title: "Oil & Gas",
    description:
      "Future-ready revenue assurance, compliance, production monitoring and operational intelligence.",
    capabilities: [
      "Revenue Assurance",
      "Compliance Monitoring",
      "Operational Reporting",
      "Industry Cloud",
    ],
  },

  {
    icon: Landmark,
    title: "Government & Public Sector",
    description:
      "Improve accountability, transparency and revenue management through digital transformation.",
    capabilities: [
      "Revenue Monitoring",
      "Compliance Controls",
      "Audit Trails",
      "Executive Dashboards",
    ],
  },
];

const Industries = () => {
  return (
    <section id="industries" className="relative overflow-hidden py-28">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <img
          src={backgroundSvg}
          alt="Background Pattern"
          className="w-full h-full object-cover opacity-25"
        />

        {/* <div className="absolute inset-0 bg-gradient-to-br from-[#0A2540]/95 to-[#0E2C44]/95" /> */}
      </div>

      {/* Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] bg-[#4CAF50]/10 blur-[180px] rounded-full" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto text-center"
        >
          <span className="inline-flex items-center rounded-full border border-[#4CAF50]/20 bg-[#4CAF50]/10 px-4 py-2 text-sm font-medium text-[#4CAF50]">
            INDUSTRIES
          </span>

          <h2 className="mt-6 text-4xl md:text-6xl font-bold tracking-tight text-gray-900">
            Built for every industry.
          </h2>

          <p className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed">
            Eaze adapts to the unique operational, financial and compliance
            requirements of different industries while maintaining a single
            source of truth across the organization.
          </p>
        </motion.div>

        {/* Industry Cards */}
        <div className="mt-20 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {industries.map((industry, index) => {
            const Icon = industry.icon;

            return (
              <motion.div
                key={industry.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.05,
                }}
                className="
                  group
                  rounded-3xl
                  border
                  border-gray-200
                  bg-white
                  p-8
                  hover:border-[#4CAF50]/30
                  hover:shadow-xl
                  transition-all
                "
              >
                <div className="w-14 h-14 rounded-2xl bg-[#4CAF50]/10 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-[#4CAF50]" />
                </div>

                <h3 className="mt-6 text-2xl font-bold text-gray-900">
                  {industry.title}
                </h3>

                <p className="mt-4 text-gray-600 leading-relaxed">
                  {industry.description}
                </p>

                <div className="mt-6 flex flex-wrap gap-2">
                  {industry.capabilities.map((item) => (
                    <span
                      key={item}
                      className="
                        px-3
                        py-2
                        rounded-full
                        bg-[#4CAF50]/10
                        text-[#4CAF50]
                        text-sm
                        font-medium
                      "
                    >
                      {item}
                    </span>
                  ))}
                </div>

                {/* <button className="mt-8 flex items-center gap-2 text-[#4CAF50] font-semibold">
                  Explore Solution
                  <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button> */}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Industries;
